

import React, { useState, useEffect } from "react";
import { Card, Button, Row, Col, Alert, ProgressBar } from "react-bootstrap";
import { FaUpload, FaCamera, FaCheck, FaTimes } from "react-icons/fa";
import CameraCapture from "./CameraCapture";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const DocumentUpload = ({ empleado }) => {
  const legajo = empleado?.legajo;
  const cuil = empleado?.cuil;

  const [images, setImages] = useState({
    selfie: null,
    dniFront: null,
    dniBack: null,
    carnetFront: null,
    carnetBack: null
  });

  const [cameraType, setCameraType] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [message, setMessage] = useState("");
  const [fotosExistentes, setFotosExistentes] = useState({});

  useEffect(() => {
    if (!legajo) return;

    // Normalizar API_URL (remover barra final si existe)
    const baseURL = API_URL.replace(/\/$/, '');
    
    // Obtener token de autenticación
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('⚠️ No hay token de autenticación');
      return;
    }
    
    fetch(`${baseURL}/api/fotos`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`Error ${res.status}: ${res.statusText}`);
        }
        return res.json();
      })
      .then(data => {
        const estado = {};
        
        // data es un array de documentos del usuario
        data.forEach(doc => {
          // Mapear tipo del backend al frontend
          const tipoKey = Object.entries({
            'Selfie': 'selfie',
            'DNI/Frente': 'dniFront',
            'DNI/Dorso': 'dniBack',
            'Carnet/Frente': 'carnetFront',
            'Carnet/Dorso': 'carnetBack'
          }).find(([display, key]) => doc.tipo === display)?.[1];
          
          if (tipoKey) {
            estado[tipoKey] = doc.url;
          }
        });

        setFotosExistentes(estado);
      })
      .catch(err => {
        console.error("❌ Error al cargar fotos existentes:", err);
      });
  }, [legajo]);


  const openCamera = (type) => {
    setCameraType(type);
    setShowCamera(true);
  };

  const handleFileSelect = (type, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Convertir archivo a base64
    const reader = new FileReader();
    reader.onload = (e) => {
      setImages(prev => ({ ...prev, [type]: e.target.result }));
      setUploadStatus(prev => ({ ...prev, [type]: null }));
    };
    reader.readAsDataURL(file);
  };

  const handleCapture = (image) => {
    if (image && cameraType) {
      setImages(prev => ({ ...prev, [cameraType]: image }));
      setUploadStatus(prev => ({ ...prev, [cameraType]: null }));
    }
  };

  const removeImage = (type) => {
    setImages(prev => ({ ...prev, [type]: null }));
    setUploadStatus(prev => ({ ...prev, [type]: null }));
  };

  const uploadAllImages = async () => {
    setLoading(true);
    setMessage("🔄 Iniciando carga de imágenes...");

    const imageTypes = Object.keys(images);
    const imagesToUpload = imageTypes.filter(type => images[type]);

    if (imagesToUpload.length === 0) {
      setMessage("⚠️ No hay imágenes para cargar");
      setLoading(false);
      return;
    }

    try {
      // Comprimir y preparar todas las imágenes primero
      const uploadPromises = imagesToUpload.map(async (type) => {
        setUploadStatus(prev => ({ ...prev, [type]: "uploading" }));

        const response = await fetch(images[type]);
        const blob = await response.blob();
        
        // Comprimir imagen antes de subir
        const compressedBlob = await compressImage(blob, 0.8); // 80% calidad
        const file = new File([compressedBlob], `${type}.jpg`, { type: "image/jpeg" });

        const formData = new FormData();
        // ⚠️ IMPORTANTE: Los campos de texto DEBEN ir ANTES del archivo
        // Multer necesita leer tipo y legajo antes de procesar el archivo
        formData.append("tipo", type);
        formData.append("legajo", legajo);
        formData.append("nombreArchivo", type === "selfie" ? `${cuil}_${type}` : `${legajo}_${type}`);
        formData.append("image", file); // Archivo al FINAL

        // Obtener token de autenticación
        const token = localStorage.getItem('token');
        
        // ⚠️ IMPORTANTE: NO establecer Content-Type manualmente con FormData
        // Axios lo configura automáticamente con el boundary correcto
        const headers = {};
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        try {
          const uploadResponse = await axios.post(
            `${API_URL}/api/fotos/upload-single`,
            formData,
            {
              headers: headers,
              timeout: 30000
            }
          );

          if (uploadResponse.data.success) {
            setUploadStatus(prev => ({ ...prev, [type]: "success" }));
            return { type, success: true };
          } else {
            setUploadStatus(prev => ({ ...prev, [type]: "error" }));
            return { type, success: false };
          }
        } catch (error) {
          console.error(`Error subiendo ${type}:`, error);
          setUploadStatus(prev => ({ ...prev, [type]: "error" }));
          return { type, success: false };
        }
      });

      // Ejecutar todas las cargas en paralelo
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(r => r.success).length;

      setLoading(false);

      if (successfulUploads === imagesToUpload.length) {
        setMessage("🎉 ¡Todas las imágenes se subieron correctamente!");
        alert("✅ Carga completada con éxito");
      } else {
        setMessage(`⚠️ Se cargaron ${successfulUploads} de ${imagesToUpload.length} imágenes`);
        alert(`Se completaron ${successfulUploads} de ${imagesToUpload.length} cargadas`);
      }
    } catch (error) {
      console.error("Error general:", error);
      setMessage("❌ Error durante la carga");
      setLoading(false);
    }
  };

  // Función para comprimir imágenes
  const compressImage = (blob, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        // Redimensionar si es muy grande (máximo 1920px de ancho)
        let width = img.width;
        let height = img.height;
        const maxWidth = 1920;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((compressedBlob) => {
          resolve(compressedBlob || blob);
        }, 'image/jpeg', quality);
      };

      img.src = URL.createObjectURL(blob);
    });
  };

  const renderStatusIcon = (type) => {
    const status = uploadStatus[type];
    if (!status) return null;

    switch (status) {
      case "success":
        return <FaCheck className="text-success ms-2" />;
      case "error":
        return <FaTimes className="text-danger ms-2" />;
      case "uploading":
        return <div className="spinner-border spinner-border-sm ms-2" />;
      default:
        return null;
    }
  };

  const renderCard = (title, type) => {
    const hasExisting = !!fotosExistentes[type];
    const hasNew = !!images[type];

    // Subtítulos descriptivos para cada tipo de documento
    const subtitles = {
      selfie: "Foto de tu rostro con fondo automático",
      dniFront: "Frente del DNI con datos visibles",
      dniBack: "Dorso del DNI con CUIL/CUIT",
      carnetFront: "Frente del carnet de afiliado",
      carnetBack: "Dorso del carnet de afiliado"
    };

    const icons = {
      selfie: "🤳",
      dniFront: "🪪",
      dniBack: "🪪",
      carnetFront: "💳",
      carnetBack: "💳"
    };

    return (
      <Card className="h-100 text-center p-3 shadow-sm border-0" style={{ borderTop: '3px solid #667eea' }}>
        <div className="mb-2">
          <span style={{ fontSize: '2rem' }}>{icons[type]}</span>
        </div>
        <h5 className="mb-1" style={{ color: '#2c3e50', fontWeight: '600' }}>
          {title} {renderStatusIcon(type)}
        </h5>
        <small className="text-muted mb-3 d-block">{subtitles[type]}</small>
        {hasExisting || hasNew ? (
          <>
            <div className="position-relative mb-3">
              <img
                src={hasNew ? images[type] : fotosExistentes[type]}
                alt={title}
                className="img-fluid rounded shadow-sm"
                style={{ maxHeight: "180px", objectFit: "cover", border: '2px solid #e9ecef' }}
              />
              {hasExisting && (
                <div 
                  className="position-absolute top-0 end-0 m-2 badge bg-success"
                  style={{ fontSize: '0.7rem' }}
                >
                  ✓ Cargado
                </div>
              )}
            </div>
            {!hasExisting && (
              <div className="d-flex justify-content-center gap-2">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeImage(type)}
                  disabled={loading}
                >
                  Eliminar
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => openCamera(type)}
                  disabled={loading}
                >
                  <FaCamera className="me-1" /> Retomar
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="d-flex flex-column gap-2 mt-auto">
            <Button
              variant="primary"
              onClick={() => openCamera(type)}
              disabled={loading}
              className="d-flex align-items-center justify-content-center gap-2"
              style={{ fontWeight: '500' }}
            >
              <FaCamera /> Tomar Foto
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={loading}
              as="label"
              htmlFor={`file-${type}`}
              className="d-flex align-items-center justify-content-center gap-2"
              style={{ cursor: 'pointer' }}
            >
              <FaUpload /> Subir Archivo
            </Button>
            <input
              id={`file-${type}`}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(type, e)}
            />
          </div>
        )}
      </Card>
    );
  };


  const totalImages = Object.values(images).filter(img => img).length;
  const successfulUploads = Object.values(uploadStatus).filter(status => status === "success").length;
  const progress = totalImages > 0 ? (successfulUploads / totalImages) * 100 : 0;

  return (
    <div className="container py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header mejorado */}
      <div className="text-center mb-4">
        <h2 className="mb-2" style={{ color: '#2c3e50', fontWeight: '700' }}>
          📄 Subir Documentación
        </h2>
        <p className="text-muted mb-3">
          Colaborador: <strong>Legajo {legajo}</strong> · CUIL <strong>{cuil}</strong>
        </p>
      </div>

      <Alert variant="info" className="mb-4 shadow-sm border-0" style={{ borderLeft: '4px solid #17a2b8' }}>
        <div className="d-flex align-items-start gap-3">
          <div style={{ fontSize: '2rem' }}>💡</div>
          <div className="flex-grow-1">
            <strong className="d-block mb-2">Cómo tomar buenas fotos de documentos:</strong>
            <Row className="g-2 mb-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary" style={{ width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
                  <small><strong>Iluminación:</strong> Buena luz natural, evita sombras</small>
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary" style={{ width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</span>
                  <small><strong>Centrado:</strong> Usa el marco verde de guía</small>
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary" style={{ width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
                  <small><strong>Enfoque:</strong> Mantén la cámara quieta</small>
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary" style={{ width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>4</span>
                  <small><strong>Legibilidad:</strong> Todos los datos visibles</small>
                </div>
              </Col>
            </Row>
            <hr className="my-2" />
            <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
              <strong style={{ fontSize: '0.9rem' }}>📷 Opciones disponibles:</strong>
              <span className="badge bg-success">✓ Tomar Foto con cámara</span>
              <span className="badge bg-secondary">✓ Subir Archivo existente</span>
            </div>
          </div>
        </div>
      </Alert>

      {message && (
        <Alert variant={
          message?.includes("❌") ? "danger" :
          message?.includes("⚠️") ? "warning" :
          "info"
        }>
          {message}
        </Alert>
      )}

      <Row className="g-4 mb-4">
        <Col md={4}>{renderCard("Selfie", "selfie")}</Col>
        <Col md={4}>{renderCard("DNI Frente", "dniFront")}</Col>
        <Col md={4}>{renderCard("DNI Dorso", "dniBack")}</Col>
        <Col md={4}>{renderCard("Carnet Frente", "carnetFront")}</Col>
        <Col md={4}>{renderCard("Carnet Dorso", "carnetBack")}</Col>
      </Row>

      {progress > 0 && (
        <div className="mb-3">
          <ProgressBar
            now={progress}
            label={`${Math.round(progress)}%`}
            variant={progress === 100 ? "success" : "primary"}
          />
          <small className="text-muted">
            {successfulUploads} de {totalImages} imágenes subidas
          </small>
        </div>
      )}

      <div className="text-center">
        <Button
          variant="success"
          size="lg"
          onClick={uploadAllImages}
          disabled={loading || totalImages === 0}
        >
          <FaUpload className="me-2" />
          {loading ? "Subiendo..." : "Subir Todas las Imágenes"}
        </Button>
      </div>

      <CameraCapture
        show={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCapture}
        title={
          cameraType === "selfie"
            ? "Tomar Selfie"
            : cameraType === "dniFront"
            ? "Capturar Frente del DNI"
            : cameraType === "dniBack"
            ? "Capturar Dorso del DNI"
            : cameraType === "carnetFront"
            ? "Capturar Frente del Carnet"
            : "Capturar Dorso del Carnet"
        }
        documentType={cameraType === "selfie" ? "selfie" : "document"}
      />
    </div>
  );
};

export default DocumentUpload;
